/**
 * Signature pad (tracker item 42, phase 3): a canvas the customer signs with
 * a finger or a mouse (pointer events), a Clear button and an export to a PNG
 * Blob the server accepts (at most 1 MB and 4096 px a side).
 *
 * Strokes are kept as points relative to the pad's size, so the drawing is
 * redrawn crisply when the pad is resized or the phone is rotated and the
 * canvas honours devicePixelRatio. `touch-action: none` keeps the page from
 * scrolling while signing. The component never throws when a canvas is not
 * available (jsdom): drawing is skipped and the strokes are still tracked.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { SIGNATURE_MAX_BYTES } from '../api/types';
import { signatureCanvasSize } from '../utils/technician';
import { secondaryButtonClass } from './formStyles';

interface Point { x: number; y: number }
type Stroke = Point[];

/** CSS pixels of ink at 1x. */
const INK_WIDTH = 2.5;
const INK_COLOR = '#111827';
/** Width used before the first measurement (and when layout reports 0). */
const FALLBACK_WIDTH = 300;
/** Export scales tried in turn until the PNG fits the size limit. */
const EXPORT_SCALES = [1, 0.5, 0.25];

export interface SignaturePadHandle {
  /** Erases the pad. */
  clear(): void;
  /** True while nothing has been drawn. */
  isEmpty(): boolean;
  /** The signature as a PNG (white background) within the server limits; null when empty or it cannot be exported. */
  toBlob(): Promise<Blob | null>;
}

export interface SignaturePadProps {
  /** CSS height of the drawing area (default 180). */
  height?: number;
  /** Ignore input and disable Clear (a request is in flight). */
  disabled?: boolean;
  /** Called when the pad goes from empty to inked and back. */
  onInkChange?: (hasInk: boolean) => void;
  /** Accessible name of the drawing area. */
  label?: string;
}

function getContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return canvas.getContext('2d');
  } catch {
    return null;
  }
}

/** Paints the strokes (relative coordinates) on a `width` x `height` backing store. */
function paint(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  cssWidth: number,
  strokes: Stroke[],
  background: string | null,
): void {
  ctx.clearRect(0, 0, width, height);
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
  }
  const lineWidth = INK_WIDTH * (width / cssWidth);
  ctx.strokeStyle = INK_COLOR;
  ctx.fillStyle = INK_COLOR;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const stroke of strokes) {
    if (stroke.length === 0) continue;
    if (stroke.length === 1) {
      ctx.beginPath();
      ctx.arc(stroke[0].x * width, stroke[0].y * height, lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(stroke[0].x * width, stroke[0].y * height);
    for (let i = 1; i < stroke.length; i += 1) ctx.lineTo(stroke[i].x * width, stroke[i].y * height);
    ctx.stroke();
  }
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad(
  { height = 180, disabled = false, onInkChange, label = 'Signature area' },
  ref,
) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Stroke[]>([]);
  const activePointer = useRef<number | null>(null);
  const cssWidth = useRef(FALLBACK_WIDTH);
  const [hasInk, setHasInk] = useState(false);
  const inkCallback = useRef(onInkChange);
  inkCallback.current = onInkChange;

  const updateInk = useCallback((next: boolean) => {
    setHasInk((current) => {
      if (current !== next) inkCallback.current?.(next);
      return next;
    });
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = getContext(canvas);
    if (ctx) paint(ctx, canvas.width, canvas.height, cssWidth.current, strokes.current, null);
  }, []);

  /** Match the backing store to the current CSS size and pixel ratio, then redraw. */
  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const measured = wrapper.getBoundingClientRect().width;
    cssWidth.current = measured > 0 ? measured : FALLBACK_WIDTH;
    const ratio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const size = signatureCanvasSize(cssWidth.current, height, ratio);
    // Setting width / height clears the canvas, so only touch them when they change.
    if (canvas.width !== size.width) canvas.width = size.width;
    if (canvas.height !== size.height) canvas.height = size.height;
    redraw();
  }, [height, redraw]);

  useEffect(() => {
    resize();
    const wrapper = wrapperRef.current;
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && wrapper) {
      observer = new ResizeObserver(() => resize());
      observer.observe(wrapper);
    }
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('orientationchange', resize);
    };
  }, [resize]);

  const clear = useCallback(() => {
    strokes.current = [];
    activePointer.current = null;
    redraw();
    updateInk(false);
  }, [redraw, updateInk]);

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => strokes.current.every((stroke) => stroke.length === 0),
    toBlob: async () => {
      if (strokes.current.every((stroke) => stroke.length === 0)) return null;
      for (const scale of EXPORT_SCALES) {
        const canvas = document.createElement('canvas');
        const ratio = (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1) * scale;
        const size = signatureCanvasSize(cssWidth.current, height, ratio);
        canvas.width = size.width;
        canvas.height = size.height;
        const ctx = getContext(canvas);
        if (!ctx) return null;
        paint(ctx, size.width, size.height, cssWidth.current, strokes.current, '#ffffff');
        const blob = await new Promise<Blob | null>((resolve) => {
          try {
            canvas.toBlob(resolve, 'image/png');
          } catch {
            resolve(null);
          }
        });
        if (blob && blob.size > 0 && blob.size <= SIGNATURE_MAX_BYTES) return blob;
      }
      return null;
    },
  }), [clear, height]);

  /** Position of a pointer event relative to the pad (0..1 each way). */
  const pointOf = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    const w = rect.width > 0 ? rect.width : cssWidth.current;
    const h = rect.height > 0 ? rect.height : height;
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    return { x: clamp((event.clientX - rect.left) / w), y: clamp((event.clientY - rect.top) / h) };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || activePointer.current !== null) return;
    // Mouse: the primary button only.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    activePointer.current = event.pointerId;
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // The pointer is already gone; drawing still works without capture.
    }
    const point = pointOf(event);
    strokes.current.push([point]);
    const canvas = canvasRef.current;
    const ctx = canvas ? getContext(canvas) : null;
    if (canvas && ctx) paint(ctx, canvas.width, canvas.height, cssWidth.current, strokes.current, null);
    updateInk(true);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointer.current !== event.pointerId) return;
    event.preventDefault();
    const stroke = strokes.current[strokes.current.length - 1];
    if (!stroke) return;
    const point = pointOf(event);
    const previous = stroke[stroke.length - 1];
    stroke.push(point);
    const canvas = canvasRef.current;
    const ctx = canvas ? getContext(canvas) : null;
    if (canvas && ctx) {
      // Only the new segment; the rest is already on the canvas.
      ctx.strokeStyle = INK_COLOR;
      ctx.lineWidth = INK_WIDTH * (canvas.width / cssWidth.current);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(previous.x * canvas.width, previous.y * canvas.height);
      ctx.lineTo(point.x * canvas.width, point.y * canvas.height);
      ctx.stroke();
    }
  };

  const endStroke = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointer.current !== event.pointerId) return;
    activePointer.current = null;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      // Already released.
    }
  };

  return (
    <div>
      <div ref={wrapperRef} className="relative w-full overflow-hidden rounded-lg border-2 border-dashed border-gray-300 bg-white">
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={label}
          data-testid="signature-canvas"
          className="block w-full touch-none select-none"
          style={{ height, touchAction: 'none' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
        />
        {!hasInk && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400" aria-hidden="true">
            Sign here
          </span>
        )}
      </div>
      <div className="mt-2">
        <button type="button" className={`${secondaryButtonClass} min-h-[44px]`} disabled={disabled || !hasInk} onClick={clear}>
          Clear
        </button>
      </div>
    </div>
  );
});
